import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Course = Database["public"]["Tables"]["courses"]["Row"] & {
  instructor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    bio: string | null;
  };
  modules?: Array<{
    id: string;
    title: string;
    description: string | null;
    order_index: number;
    lessons: Array<{
      id: string;
      title: string;
      description: string | null;
      lesson_type: string;
      duration_minutes: number | null;
      is_preview: boolean;
      order_index: number;
    }>;
  }>;
  enrollment_count?: number;
  average_rating?: number;
  review_count?: number;
  reviews?: Array<{
    id: string;
    rating: number;
    comment: string | null;
    created_at: string;
    user: {
      full_name: string;
      avatar_url: string | null;
    };
  }>;
  is_enrolled?: boolean;
};

export const useCourseDetail = (slug: string) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCourseDetail();
  }, [slug]);

  const fetchCourseDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Fetch course with instructor
      const { data: courseData, error: courseError } = await supabase
        .from("courses")
        .select(`
          *,
          instructor:profiles!courses_instructor_id_fkey(
            id,
            full_name,
            avatar_url,
            bio
          )
        `)
        .eq("slug", slug)
        .eq("status", "published")
        .single();

      if (courseError) throw courseError;
      if (!courseData) throw new Error("Course not found");

      // Fetch modules with lessons
      const { data: modulesData, error: modulesError } = await supabase
        .from("modules")
        .select(`
          id,
          title,
          description,
          order_index,
          lessons(
            id,
            title,
            description,
            lesson_type,
            duration_minutes,
            is_preview,
            order_index
          )
        `)
        .eq("course_id", courseData.id)
        .order("order_index", { ascending: true });

      if (modulesError) throw modulesError;

      // Sort lessons within each module
      const sortedModules = modulesData?.map(module => ({
        ...module,
        lessons: (module.lessons as any[]).sort((a, b) => a.order_index - b.order_index)
      }));

      // Fetch enrollment count
      const { count: enrollmentCount } = await supabase
        .from("enrollments")
        .select("*", { count: "exact", head: true })
        .eq("course_id", courseData.id);

      // Fetch reviews with user profiles
      const { data: reviewsData } = await supabase
        .from("reviews")
        .select(`
          id,
          rating,
          comment,
          created_at,
          user:profiles!reviews_user_id_fkey(
            full_name,
            avatar_url
          )
        `)
        .eq("course_id", courseData.id)
        .order("created_at", { ascending: false });

      // Calculate average rating
      const ratings = reviewsData?.map(r => r.rating) || [];
      const averageRating = ratings.length > 0
        ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
        : 0;

      // Check if user is enrolled
      let isEnrolled = false;
      if (user) {
        const { data: enrollmentData } = await supabase
          .from("enrollments")
          .select("id")
          .eq("user_id", user.id)
          .eq("course_id", courseData.id)
          .maybeSingle();

        isEnrolled = !!enrollmentData;
      }

      setCourse({
        ...courseData,
        modules: sortedModules || [],
        enrollment_count: enrollmentCount || 0,
        average_rating: averageRating,
        review_count: ratings.length,
        reviews: reviewsData || [],
        is_enrolled: isEnrolled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch course");
      console.error("Error fetching course:", err);
    } finally {
      setLoading(false);
    }
  };

  const enrollInCourse = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!course) throw new Error("No course loaded");

      const { error } = await supabase
        .from("enrollments")
        .insert({
          user_id: user.id,
          course_id: course.id,
          status: "active",
          progress: 0,
        });

      if (error) throw error;

      // Refresh course data
      await fetchCourseDetail();
      return { success: true };
    } catch (err) {
      console.error("Error enrolling:", err);
      return { success: false, error: err instanceof Error ? err.message : "Enrollment failed" };
    }
  };

  const submitReview = async (rating: number, comment: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!course) throw new Error("No course loaded");

      const { error } = await supabase
        .from("reviews")
        .insert({
          user_id: user.id,
          course_id: course.id,
          rating,
          comment,
        });

      if (error) throw error;

      // Refresh course data
      await fetchCourseDetail();
      return { success: true };
    } catch (err) {
      console.error("Error submitting review:", err);
      return { success: false, error: err instanceof Error ? err.message : "Review submission failed" };
    }
  };

  return {
    course,
    loading,
    error,
    enrollInCourse,
    submitReview,
    refetch: fetchCourseDetail,
  };
};
